<template>
  <div class="md:max-w-lg mx-auto">
    <div ref="listContainer" class="space-y-2 min-h-[400px]">
      <!-- Only render the items up to the current index -->
      <Motion
        v-for="data in itemsToShow"
        :key="data.id"
        as="div"
        :initial="{ scale: 0, opacity: 0 }"
        :animate="{
          scale: 1,
          opacity: 1,
          y: 0,
        }"
        :exit="{
          scale: 0,
          opacity: 0,
          y: 0,
        }"
        :transition="{
          type: 'spring',
          stiffness: 350,
          damping: 40,
        }"
        class="w-full"
      >
        <Notification
          :title="data.title"
          :url="data.url"
          :favicon="data.favicon"
          :found-on="data.foundOn"
          :found-on-website="data.foundOnWebsite"
          :found-through="data.foundThrough"
          :date="data.date"
          :status-text="data.statusText"
          :removal-type="data.removalType"
        />
      </Motion>
    </div>
  </div>
</template>

<script setup>
import { Motion } from "motion-v";
import { computed, onMounted, onBeforeUnmount, ref } from "vue";
import Notification from "./Notification.vue";

const props = defineProps({
  locale: {
    type: String,
    default: 'en'
  },
  delay: {
    type: Number,
    default: 2000
  }
});

const displayedItems = ref([]);
const nextIndex = ref(0);
const listContainer = ref(null);
const hasAnimated = ref(false);
let observer = null;

// Translations for leak data
const translations = computed(() => ({
  found: props.locale === 'de' ? 'Gefunden' : 'Found',
  foundOnWebsite: props.locale === 'de' ? 'Auf Website gefunden' : 'Found on Website',
  foundThrough: props.locale === 'de' ? 'Gefunden über' : 'Found through',
  directRemoval: props.locale === 'de' ? 'Direkte Website-Entfernung' : 'Direct Website Removal',
  searchEngineRemoval: props.locale === 'de' ? 'Entfernung bei Suchmaschine' : 'Removal on Search Engine',
  scanning: props.locale === 'de' ? 'Weitere Leaks werden gescannt...' : 'Scanning for more leaks...'
}));

// Leak data adapted for animated list
const leakData = computed(() => {
  const today = new Date();
  const todayString = props.locale === 'de' 
    ? today.toLocaleDateString('de-DE') 
    : today.toLocaleDateString('en-US');
  
  const titles = props.locale === 'de' ? [
    'Jane Doe Nacktbilder OnlyFans Foto #30 – Nudostar',
    'Jane Doe private Bilder durchgesickert – Nudogram v2.1',
    'Jane Doe exklusiver OnlyFans Content – Fapello'
  ] : [
    'Jane Doe Nude OnlyFans Photo #30 – Nudostar',
    'Jane Doe Nude Leaks OnlyFans Photo #79 - Nudogram v2.1',
    'Jane Doe Exclusive OnlyFans Content – Fapello'
  ];
  
  return [
    {
      title: titles[0],
      url: 'https://nudostar.com/content/m/a/jane-doe/1000/jane-doe_0009_340.jpg',
      favicon: 'https://www.google.com/s2/favicons?domain=https%3A%2F%2Fnudostar.com',
      foundOn: 'website',
      foundOnWebsite: translations.value.foundOnWebsite,
      foundThrough: translations.value.foundThrough,
      date: todayString,
      statusText: translations.value.found,
      removalType: translations.value.directRemoval
    },
    {
      title: titles[1],
      url: 'https://nudogram.com/contents/m/a/jane-doe/1000/jane-doe_0079.jpg',
      favicon: 'https://www.google.com/s2/favicons?domain=https%3A%2F%2Fnudogram.com',
      foundOn: 'google',
      foundOnWebsite: translations.value.foundOnWebsite,
      foundThrough: translations.value.foundThrough,
      date: todayString,
      statusText: translations.value.found,
      removalType: translations.value.searchEngineRemoval
    },
    {
      title: titles[2],
      url: 'https://fapello.com/content/m/a/jane-doe/1000/jane-doe_0050.jpg',
      favicon: 'https://www.google.com/s2/favicons?domain=https%3A%2F%2Ffapello.com',
      foundOn: 'website',
      foundOnWebsite: translations.value.foundOnWebsite,
      foundThrough: translations.value.foundThrough,
      date: todayString,
      statusText: translations.value.found,
      removalType: translations.value.directRemoval
    },
  ];
});

onMounted(() => {
  // Set up intersection observer to trigger animation when component becomes visible
  if (listContainer.value) {
    observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && !hasAnimated.value) {
            hasAnimated.value = true;
            setTimeout(() => {
              startLoop();
            }, 500);
          }
        });
      },
      {
        threshold: 0.3, // Trigger when 30% of the component is visible
        rootMargin: '0px 0px -50px 0px' // Start animation slightly before fully visible
      }
    );
    
    observer.observe(listContainer.value);
  }
});

onBeforeUnmount(() => {
  if (observer) {
    observer.disconnect();
  }
});

async function startLoop() {
  const notifications = leakData.value;
  if (!notifications.length) return;

  const maxItems = 3; // Fixed number of items to prevent layout shift
  
  // Start with empty list, then add items one by one
  displayedItems.value = [];
  
  // Add initial items one by one
  for (let i = 0; i < maxItems; i++) {
    displayedItems.value.unshift({
      ...notifications[i % notifications.length],
      id: `${i}-${Date.now()}`,
    });
    await wait(props.delay);
  }

  // Infinite loop - replace items one by one
  while (true) {
    // Remove the last item and add a new one at the top
    displayedItems.value.pop();
    displayedItems.value.unshift({
      ...notifications[nextIndex.value % notifications.length],
      id: `${nextIndex.value}-${Date.now()}`,
    });
    nextIndex.value = nextIndex.value + 1;
    await wait(props.delay);
  }
}

const itemsToShow = computed(() => displayedItems.value);

async function wait(ms) {
  return new Promise((resolve) => setTimeout(resolve, ms));
}
</script>

<style scoped>
/* Responsive adjustments */
@media (max-width: 640px) {
  .w-full {
    padding: 0 1rem;
  }
}
</style>